import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Smartphone, Copy, ExternalLink } from "lucide-react";

export const PhoneCalendarButton = () => {
  const [open, setOpen] = useState(false);
  const { data: feed } = useQuery({
    queryKey: ["my-cal-feed"],
    queryFn: async () => (await api.get("/calendar/my-feed")).data,
    enabled: open,
  });

  const copy = async () => {
    await navigator.clipboard.writeText(feed?.https_url || "");
    toast.success("Calendar link copied");
  };

  return (
    <>
      <Button data-testid="phone-calendar-btn" variant="outline" onClick={() => setOpen(true)} className="rounded-sm h-10">
        <Smartphone size={15} className="mr-2" /> Phone calendar
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="phone-calendar-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Sync to your phone calendar</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Subscribe once and every booking &amp; task assigned to you lands in your phone calendar automatically — with reminders.
          </p>
          <div className="flex gap-2 mt-1">
            <Input readOnly value={feed?.https_url || "Loading…"} className="rounded-sm h-10 font-mono text-xs" data-testid="calendar-feed-url" />
            <Button variant="outline" onClick={copy} className="rounded-sm h-10 px-3" data-testid="copy-feed-btn"><Copy size={14} /></Button>
          </div>
          {feed?.webcal_url && (
            <a href={feed.webcal_url} className="inline-flex items-center gap-1.5 text-sm text-cyan-600 hover:underline font-medium" data-testid="webcal-link">
              <ExternalLink size={14} /> Open directly in phone calendar (iPhone)
            </a>
          )}
          <div className="text-xs text-slate-500 space-y-1.5 mt-1">
            <p><strong>iPhone:</strong> tap the link above, or Settings → Apps → Calendar → Accounts → Add Subscribed Calendar → paste the link.</p>
            <p><strong>Android / Google Calendar:</strong> calendar.google.com → Settings → Add calendar → From URL → paste the link.</p>
            <p>This link is personal to your account — don't share it.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
